import React, { useEffect, useState } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { RestaurantMenu } from '@mui/icons-material';
import { nutritionV2API } from '../../services/api';
import { tokens } from '../../theme';
import { useAuth } from '../../contexts/AuthContext';

export default function NutritionInsightsPanel({ userId }) {
  const { previewMode } = useAuth();
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // previewMode comes from AuthContext (captured once at session
    // establishment), not re-derived from window.location.search -- ordinary
    // in-app navigation rewrites the URL without the query string, so a
    // guard that re-read it here would re-arm on the very first nav click.
    // The preview user id ('preview-user') is truthy, so this would
    // otherwise fire for a logged-out preview visitor, 401, and trigger the
    // auth redirect that kicks them to the login page.
    if (!userId || previewMode) return undefined;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const res = await nutritionV2API.getInsights(userId, { lookbackDays: 14 });
        if (!cancelled) setInsights(res.data?.data || null);
      } catch {
        if (!cancelled) setInsights(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [previewMode, userId]);

  if (loading || !insights?.coaching?.length) return null;

  const topCoaching = insights.coaching[0];
  const topTrigger = insights.cravingTriggers?.[0];

  return (
    <Box
      sx={{
        mt: 1.2,
        p: 1.35,
        border: '1px solid rgba(197, 215, 152, 0.16)',
        backgroundColor: 'rgba(255,255,255,0.02)',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
        <RestaurantMenu sx={{ color: tokens.accentStrong, fontSize: 18 }} />
        <Typography variant="subtitle2" sx={{ color: tokens.cream, fontWeight: 700 }}>
          Nutrition coaching
        </Typography>
        {insights.preferences?.proteinTargetG && (
          <Chip size="small" variant="outlined" label={`${insights.preferences.proteinTargetG}g protein target`} />
        )}
      </Stack>
      <Typography variant="caption" sx={{ color: tokens.textSoft, display: 'block' }}>
        {topCoaching.message}
      </Typography>
      {topTrigger && (
        <Typography variant="caption" sx={{ color: tokens.textMuted, display: 'block', mt: 0.5 }}>
          Top craving trigger: {topTrigger.trigger} ({topTrigger.count}x)
        </Typography>
      )}
    </Box>
  );
}
